import { DateFilter, MonthFilter, YearFilter } from '@quality24/design-system';
import moment from 'moment';
import * as React from 'react';

import { DateFilter as DateFilterType } from '../../@types';
import { useQueryFilter } from '../../hooks/filterHooks';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'> {
  defaultFrom?: string;
  defaultTo?: string;
  minDate?: Date;
  maxDate?: Date;
}

const MIN_DATE = moment('2018-01-01').toDate();

/**
 * <DateFilterContainer> component.
 */
export const DateFilterContainer: React.FunctionComponent<Props> = ({
  className,
  style,
  defaultFrom = moment().startOf('month').toISOString(),
  defaultTo = moment().endOf('day').toISOString(),
  minDate,
  maxDate,
  ...htmlElementProps
}) => {
  const { filters, upsertFilter, dateFilterMode } = useQueryFilter([
    'createdAt',
  ]);
  const fallbackPeriod = React.useMemo(() => {
    if (dateFilterMode === 'year') {
      return {
        from: moment().startOf('year').toISOString(),
        to: moment().endOf('year').toISOString(),
      };
    }

    if (dateFilterMode === 'month') {
      return {
        from: moment().startOf('month').toISOString(),
        to: moment().endOf('month').toISOString(),
      };
    }

    return {
      from: defaultFrom,
      to: defaultTo,
    };
  }, [dateFilterMode, defaultFrom, defaultTo]);

  // Memo the start and end dates
  const period = React.useMemo(() => {
    const filterValue = filters.createdAt;
    if (!filterValue || Array.isArray(filterValue)) {
      // eslint-disable-next-line no-console
      console.warn('There was something wrong with the date filter');
      return fallbackPeriod;
    }

    return {
      from:
        (filterValue as DateFilterType).greaterThanOrEqualTo ||
        fallbackPeriod.from,
      to:
        (filterValue as DateFilterType).lessThanOrEqualTo || fallbackPeriod.to,
    };
  }, [filters.createdAt, fallbackPeriod]);

  /**
   * Handles the filter change, saving it the the context
   */
  const handleFilterChange = React.useCallback(
    (from: string, to: string) => {
      upsertFilter('createdAt', {
        greaterThanOrEqualTo: from,
        lessThanOrEqualTo: to,
      });
    },
    [upsertFilter],
  );

  const resolvedMinDate = minDate || MIN_DATE;
  const resolvedMaxDate = maxDate || moment().toDate();

  if (dateFilterMode === 'year') {
    return (
      <YearFilter
        className={className}
        style={style}
        from={period.from}
        to={period.to}
        onSubmit={handleFilterChange}
        minDate={resolvedMinDate}
        maxDate={resolvedMaxDate}
        {...htmlElementProps}
      />
    );
  }

  return dateFilterMode === 'date' ? (
    <DateFilter
      isRangePicker
      className={className}
      style={style}
      from={period.from}
      to={period.to}
      onSubmit={handleFilterChange}
      minDate={resolvedMinDate}
      maxDate={resolvedMaxDate}
      {...htmlElementProps}
    />
  ) : (
    <MonthFilter
      isRangePicker
      className={className}
      style={style}
      from={period.from}
      to={period.to}
      onSubmit={handleFilterChange}
      minDate={resolvedMinDate}
      maxDate={resolvedMaxDate}
      {...htmlElementProps}
    />
  );
};

export default DateFilterContainer;
